// pages/Thread.jsx
import React, { useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiFetch } from "../api";
import { useAuth } from "../context/AuthContext";
import { getAvatarUrl } from "../constants/avatars";
import "../styles/pages/_thread.css";
import { timeAgo, formatDate, formatDateTime, TimeAgo } from "../helpers/time";

// Component

export default function Thread() {
    const { threadId }                   = useParams();
    const { user }                       = useAuth();
    const [thread, setThread]            = useState(null);
    const [messages, setMessages]        = useState([]);
    const [loading, setLoading]          = useState(true);
    const [error, setError]              = useState(null);

    // Reply state
    const [replyContent, setReplyContent]   = useState("");
    const [replyPreview, setReplyPreview]   = useState(false);
    const [replySubmitting, setReplySubmitting] = useState(false);
    const [replyError, setReplyError]       = useState(null);

    // Edit state
    const [editingId, setEditingId]         = useState(null);
    const [editContent, setEditContent]     = useState("");
    const [editPreview, setEditPreview]     = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);

    // Load thread data

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setError(null);
            try {
                const data = await apiFetch(`/api/forum/threads/${threadId}`);
                if (!cancelled) {
                    setThread(data.thread);
                    setMessages(data.messages);
                }
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [threadId]);

    // Reply handler

    async function handleReply(e) {
        e.preventDefault();
        setReplyError(null);

        if (!replyContent.trim()) {
            setReplyError("Post content is required.");
            return;
        }

        setReplySubmitting(true);
        try {
            const data = await apiFetch(`/api/forum/threads/${threadId}/messages`, {
                method: "POST",
                body: JSON.stringify({ content: replyContent.trim() }),
            });
            setMessages((prev) => [...prev, data.message]);
            setReplyContent("");
            setReplyPreview(false);
        } catch (err) {
            setReplyError(err.message);
        } finally {
            setReplySubmitting(false);
        }
    }

    // Edit handlers

    function startEdit(msg) {
        setEditingId(msg.id);
        setEditContent(msg.content);
        setEditPreview(false);
    }

    function cancelEdit() {
        setEditingId(null);
        setEditContent("");
        setEditPreview(false);
    }

    async function saveEdit(msgId) {
        if (!editContent.trim()) return;
        setEditSubmitting(true);
        try {
            const data = await apiFetch(`/api/forum/messages/${msgId}`, {
                method: "PUT",
                body: JSON.stringify({ content: editContent.trim() }),
            });
            setMessages((prev) =>
                prev.map((m) =>
                    m.id === msgId
                        ? { ...m, content: data.message.content, edited_at: data.message.edited_at }
                        : m
                )
            );
            cancelEdit();
        } catch (err) {
            alert(err.message);
        } finally {
            setEditSubmitting(false);
        }
    }

    // Delete handler

    async function handleDelete(msgId) {
        if (!window.confirm("Are you sure you want to delete this post?")) return;
        try {
            await apiFetch(`/api/forum/messages/${msgId}`, { method: "DELETE" });
            setMessages((prev) => prev.filter((m) => m.id !== msgId));
        } catch (err) {
            alert(err.message);
        }
    }

    // Loading / Error

    if (loading) {
        return (
            <div className="wrapper thread">
                <div className="thread__loading">Loading thread…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="wrapper thread">
                <Link to="/forum" className="thread__back">← Back to Forum</Link>
                <div className="thread__error">
                    <h2>Thread not found</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    const isStaff = user && (user.role === "admin" || user.role === "moderator");

    // Main render

    return (
        <div className="wrapper thread">
            {/* Breadcrumb */}
            <div className="thread__breadcrumb">
                <Link to="/forum">Forum</Link>
                <span className="thread__breadcrumb-sep">›</span>
                <Link to={`/forum/${thread.category_id}`}>
                    {thread.category_name}
                </Link>
                <span className="thread__breadcrumb-sep">›</span>
                <span>{thread.title}</span>
            </div>

            {/* Thread header */}
            <header className="thread__header">
                <h1 className="thread__title">
                    {thread.pinned && <span className="thread__badge thread__badge--pinned">Pinned</span>}
                    {thread.locked && <span className="thread__badge thread__badge--locked">Locked</span>}
                    {thread.title}
                </h1>
                <div className="thread__meta">
                    <span>Started by <strong>{thread.author_name}</strong></span>
                    <span className="thread__meta-sep">·</span>
                    <span>{formatDateTime(thread.created_at)}</span>
                </div>
            </header>

            {/* Messages / Posts */}
            <div className="thread__messages">
                {messages.map((msg, idx) => (
                    <div key={msg.id} className="thread__post">
                        {/* Author sidebar */}
                        <div className="thread__post-author">
                            <Link
                                to={`/profile/${msg.account_id}`}
                                className="thread__post-avatar-link"
                            >
                                <img
                                    src={getAvatarUrl(msg.author_avatar)}
                                    alt={msg.author_name}
                                    className="thread__post-avatar"
                                />
                            </Link>
                            <Link
                                to={`/profile/${msg.account_id}`}
                                className="thread__post-author-name"
                            >
                                {msg.author_name || "Deleted User"}
                            </Link>
                            {msg.author_title && (
                                <span className="thread__post-author-title">{msg.author_title}</span>
                            )}
                            <span className={`role role--${msg.author_role}`}>{msg.author_role}</span>
                            <span className="thread__post-author-posts">
                                {msg.author_post_count} post{msg.author_post_count !== 1 ? "s" : ""}
                            </span>
                        </div>

                        {/* Post content */}
                        <div className="thread__post-body">
                            <div className="thread__post-header">
                                <span className="thread__post-date">
                                    {formatDateTime(msg.created_at)}
                                </span>
                                <span className="thread__post-number">#{idx + 1}</span>
                            </div>

                            {editingId === msg.id ? (
                                /* Edit mode */
                                <div className="thread__post-edit">
                                    <div className="thread__editor-tabs">
                                        <button
                                            type="button"
                                            className={`thread__editor-tab ${!editPreview ? "thread__editor-tab--active" : ""}`}
                                            onClick={() => setEditPreview(false)}
                                        >
                                            Write
                                        </button>
                                        <button
                                            type="button"
                                            className={`thread__editor-tab ${editPreview ? "thread__editor-tab--active" : ""}`}
                                            onClick={() => setEditPreview(true)}
                                        >
                                            Preview
                                        </button>
                                    </div>
                                    {editPreview ? (
                                        <div className="thread__post-content markdown-body">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {editContent}
                                            </ReactMarkdown>
                                        </div>
                                    ) : (
                                        <textarea
                                            className="thread__edit-textarea"
                                            value={editContent}
                                            onChange={(e) => setEditContent(e.target.value)}
                                            rows={6}
                                            disabled={editSubmitting}
                                        />
                                    )}
                                    <div className="thread__edit-actions">
                                        <a
                                            className="btn btn--danger"
                                            onClick={() => saveEdit(msg.id)}
                                            disabled={editSubmitting}
                                        >
                                            {editSubmitting ? "Saving…" : "Save"}
                                        </a>
                                        <a
                                            className="btn"
                                            onClick={cancelEdit}
                                            disabled={editSubmitting}
                                        >
                                            Cancel
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                /* Normal display */
                                <>
                                    <div className="thread__post-content markdown-body">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {msg.content}
                                        </ReactMarkdown>
                                    </div>

                                    {msg.edited_at && (
                                        <div className="thread__post-edited">
                                            Edited {timeAgo(msg.edited_at)}
                                        </div>
                                    )}

                                    {msg.author_signature && (
                                        <div className="thread__post-signature">
                                            <div className="thread__post-signature-divider" />
                                            <span>{msg.author_signature}</span>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Action buttons */}
                            {user && editingId !== msg.id && (
                                <div className="thread__post-actions">
                                    {(user.id === msg.account_id || isStaff) && (
                                        <>
                                            <button
                                                className="thread__post-action"
                                                onClick={() => startEdit(msg)}
                                            >
                                                Edit
                                            </button>
                                            {idx > 0 && (
                                                <button
                                                    className="thread__post-action thread__post-action--danger"
                                                    onClick={() => handleDelete(msg.id)}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Reply box */}
            {user && !thread.locked ? (
                <div className="thread__reply">
                    <h2 className="thread__reply-title">Post a Reply</h2>
                    {replyError && (
                        <div className="thread__reply-error">{replyError}</div>
                    )}
                    <form onSubmit={handleReply}>
                        <div className="thread__reply-tabs">
                            <button
                                type="button"
                                className={`thread__editor-tab ${!replyPreview ? "thread__editor-tab--active" : ""}`}
                                onClick={() => setReplyPreview(false)}
                            >
                                Write
                            </button>
                            <button
                                type="button"
                                className={`thread__editor-tab ${replyPreview ? "thread__editor-tab--active" : ""}`}
                                onClick={() => setReplyPreview(true)}
                            >
                                Preview
                            </button>
                        </div>

                        {replyPreview ? (
                            <div className="thread__reply-preview markdown-body">
                                {replyContent.trim() ? (
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {replyContent}
                                    </ReactMarkdown>
                                ) : (
                                    <p className="thread__reply-preview-empty">Nothing to preview.</p>
                                )}
                            </div>
                        ) : (
                            <textarea
                                className="thread__reply-textarea"
                                placeholder="Write your reply using Markdown…"
                                value={replyContent}
                                onChange={(e) => setReplyContent(e.target.value)}
                                maxLength={1000}
                                rows={7}
                                disabled={replySubmitting}
                            />
                        )}

                        <div className="thread__reply-actions">
                            <p className="thread__reply-hint">
                                Supports Markdown: **bold**, *italic*, `code`, lists, links, etc.
                            </p>
                            <button
                                type="submit"
                                className="btn btn--danger"
                                disabled={replySubmitting}
                            >
                                {replySubmitting ? "Posting…" : "Post Reply"}
                            </button>
                        </div>
                    </form>
                </div>
            ) : thread.locked ? (
                <div className="thread__locked-notice">
                    🔒 This thread is locked. No new replies can be posted.
                </div>
            ) : (
                <div className="thread__login-notice">
                    <Link to="/login">Log in</Link> to reply to this thread.
                </div>
            )}
        </div>
    );
}